import { compute, ExpressionError, type Scope } from './expression';
import { countSigFigs, roundToSigFigs, SigFigError } from './sigfigs';

/**
 * The scientific calculator.
 *
 * It is the same hand-written evaluator the problem templates use, so there is
 * still no `eval`, no `Function`, and no way for a typed expression to reach
 * anything outside these files. What this module adds on top is the part that
 * makes it a chemistry calculator rather than an arithmetic one:
 *
 *  - the symbols a keypad produces (× ÷ − √ π) are translated to the grammar,
 *  - `molarMass(Ca(OH)2)` works, parentheses in the formula and all,
 *  - `ans` carries the previous result,
 *  - and the answer is also shown to the significant figures the inputs carry,
 *    because that is what the marks are for.
 */

export class CalculatorError extends Error {}

export interface CalcOutcome {
  value: number;
  /** The full value, to as many digits as it is worth printing. */
  display: string;
  /** Significant figures implied by the typed numbers, when they imply any. */
  sigfigs: number | null;
  /** The value rounded to those figures. */
  sigfigDisplay: string | null;
  /** True when a bare integer like 100 made the figure count a guess. */
  ambiguous: boolean;
}

/** Keypad symbols, and the few spellings worth accepting, mapped to grammar. */
function normalise(input: string): string {
  return input
    .replace(/[×·∙]/g, '*')
    .replace(/[÷]/g, '/')
    .replace(/[−–—]/g, '-')
    .replace(/[   ]/g, ' ')
    .replace(/√\s*(?=[\d.a-zA-Z])/g, 'sqrt(')
    .replace(/√/g, 'sqrt')
    .replace(/π/g, 'pi')
    .replace(/\blog\s*\(/g, 'log10(');
}

const NUMBER_LITERAL = /(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g;

/**
 * Pulls each `molarMass(...)` formula out into a variable holding its text.
 *
 * The evaluator takes a formula as a string, and a formula with parentheses —
 * Ca(OH)2, Fe2(SO4)3 — is not a single name, so it cannot be written inline.
 * Lifting it into a scope variable is what lets it be typed the normal way.
 */
function liftFormulas(input: string): { expression: string; scope: Scope } {
  const scope: Scope = {};
  let out = '';
  let i = 0;
  let n = 0;

  for (;;) {
    const at = input.indexOf('molarMass(', i);
    if (at === -1) {
      out += input.slice(i);
      return { expression: out, scope };
    }
    out += input.slice(i, at);

    // Walk to the matching close paren, so nested ones stay with the formula.
    let depth = 0;
    let j = at + 'molarMass('.length - 1;
    for (; j < input.length; j += 1) {
      if (input[j] === '(') depth += 1;
      else if (input[j] === ')') {
        depth -= 1;
        if (depth === 0) break;
      }
    }
    if (depth !== 0) throw new CalculatorError('A "molarMass(" is never closed.');

    const formula = input.slice(at + 'molarMass('.length, j).trim();
    if (!formula) throw new CalculatorError('molarMass needs a formula, like molarMass(H2O).');
    const name = `__f${'abcdefghijklmnopqrstuvwxyz'[n % 26]}${'z'.repeat(Math.floor(n++ / 26))}`;
    scope[name] = formula;
    out += `molarMass(${name})`;
    i = j + 1;
  }
}

/**
 * The significant figures the typed numbers carry, by the multiply-divide rule
 * of the fewest. It is the right rule most of the time and the wrong one for
 * pure addition, so the screen offers it rather than asserting it.
 */
function sigfigsOf(expression: string): { figures: number | null; ambiguous: boolean } {
  let figures: number | null = null;
  let ambiguous = false;

  for (const match of expression.matchAll(NUMBER_LITERAL)) {
    try {
      const counted = countSigFigs(match[0]);
      if (counted.ambiguous) ambiguous = true;
      figures = figures === null ? counted.count : Math.min(figures, counted.count);
    } catch {
      // Not a number on its own; the evaluator will have its own say.
    }
  }
  return { figures, ambiguous };
}

/** Prints a value in full, without float noise and without pointless zeros. */
export function formatValue(value: number): string {
  if (value === 0) return '0';
  const magnitude = Math.abs(value);
  if (magnitude >= 1e10 || magnitude < 1e-4) {
    const [mantissa = '', exponent = ''] = value.toExponential(9).split('e');
    return `${trimZeros(mantissa)}e${exponent}`;
  }
  // 12 digits is past anything a chemistry problem carries, and short of where
  // binary floating point starts printing 0.30000000000000004.
  return trimZeros(Number(value.toPrecision(12)).toString());
}

function trimZeros(text: string): string {
  return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
}

/**
 * Closes brackets left open, the way a pocket calculator does when you press
 * equals. "sqrt(16" and "2*(3+4" are what a keypad produces when you stop
 * typing at the answer, and refusing them would only be pedantry.
 */
function autoClose(expression: string): string {
  let depth = 0;
  for (const ch of expression) {
    if (ch === '(') depth += 1;
    else if (ch === ')') depth -= 1;
  }
  return depth > 0 ? expression + ')'.repeat(depth) : expression;
}

export interface CalculateOptions {
  /** The previous result, available as `ans`. */
  ans?: number;
}

export function calculate(input: string, { ans }: CalculateOptions = {}): CalcOutcome {
  const text = normalise(input).trim();
  if (!text) throw new CalculatorError('Nothing to work out yet.');

  const { expression, scope } = liftFormulas(autoClose(text));
  const constants: Scope = { pi: Math.PI, e: Math.E, ...scope };
  if (ans !== undefined && Number.isFinite(ans)) constants.ans = ans;

  let value: number;
  try {
    value = compute(expression, constants);
  } catch (err) {
    if (err instanceof ExpressionError) {
      // "ans" reads as an ordinary variable, so say what it actually means.
      const message =
        /"ans" has no value/.test(err.message)
          ? 'There is no previous answer to use yet.'
          : err.message;
      throw new CalculatorError(message);
    }
    throw new CalculatorError(err instanceof Error ? err.message : 'That did not work out.');
  }

  const { figures, ambiguous } = sigfigsOf(expression);
  let sigfigDisplay: string | null = null;
  if (figures !== null) {
    try {
      sigfigDisplay = roundToSigFigs(value, Math.min(figures, 15));
    } catch (err) {
      if (!(err instanceof SigFigError)) throw err;
    }
  }

  return {
    value,
    display: formatValue(value),
    sigfigs: figures,
    sigfigDisplay,
    ambiguous,
  };
}
